import React, { useState, useEffect } from 'react';
import { Row, Col, Table } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { ButtonTooltip, DefaultModal, Icon, DatatableEmployeeName, CompactCard } from '../../components';
import { camelCaseEmptyStringRecursive } from '../../services/utils';
import { HRVacationsRequest } from '../../requests/dashboards';
import VacationDataTable from '../Shared/Vacation/VacationDataTable';
import columns from '../Shared/Vacation/approveColumns';
import availableVacationColumns from '../Shared/Vacation/availableVacationColumns';
import VacationAvailableDataTable from '../Shared/Vacation/VacationAvailableDataTable';

const VacationIndex = ({ moreData, setMoreData, todayMoment = null, twoMonthsAgo = null }) => {
  const [cards, setCards] = useState({});
  const [customParams, setCustomParams] = useState({ vacationsAvailableType: 'legal' });
  const [activeCard, setActiveCard] = useState('totalLegalVacationsAvailable');
  const [defaultStartDate, setDefaultStartDate] = useState(twoMonthsAgo);
  const [defaultEndDate, setDefaultEndDate] = useState(todayMoment);
  const [modalShow, setModalShow] = useState(false);
  const [modalBody, setModalBody] = useState('');
  const dispatch = useDispatch();
  const history = useHistory();

  const fetchDashboardCards = () => {
    const request = async () => {
      HRVacationsRequest({
        dispatch,
        successCallback: response => {
          setCards(camelCaseEmptyStringRecursive(response.data));
        }
      });
    };
    request();
  };

  const handleClick = card => {
    switch (card) {
      case 'pending':
        setCustomParams({ status: 'pending' });
        setActiveCard('pending');
        setDefaultStartDate(moment().subtract(2, 'months'));
        setDefaultEndDate(moment());
        break;
      case 'totalProgressiveVacationsAvailable':
        setCustomParams({ vacationsAvailableType: 'progressive' });
        setActiveCard('totalProgressiveVacationsAvailable');
        setDefaultStartDate(moment().subtract(2, 'months'));
        setDefaultEndDate(moment());
        break;
      case 'rejected':
        setCustomParams({ status: 'rejected' });
        setActiveCard('rejected');
        setDefaultStartDate(moment().subtract(2, 'months'));
        setDefaultEndDate(moment());
        break;
      case 'onVacation':
        setCustomParams({ status: 'approved' });
        setActiveCard('onVacation');
        setDefaultStartDate(moment().startOf('day'));
        setDefaultEndDate(moment().endOf('day'));
        break;
      case 'differencePreviousYear':
        setCustomParams({ status: 'approved' });
        setActiveCard('differencePreviousYear');
        setDefaultStartDate(moment().startOf('year'));
        setDefaultEndDate(moment().endOf('day'));
        break;
      default:
        setCustomParams({ vacationsAvailableType: 'legal' });
        setActiveCard('totalLegalVacationsAvailable');
        setDefaultStartDate(moment().subtract(2, 'months'));
        setDefaultEndDate(moment());
        break;
    }
    setMoreData(!moreData);
  };

  const redirectToEmployee = (employee, tab) => {
    history.push(`/employees/${employee.id}${tab ? `?tab=${tab}` : ''}`);
  };

  const openModal = tab => {
    if (cards.twoPeriodsAcumulated) {
      const tableBody =
        cards.twoPeriodsAcumulated.amount > 0 ? (
          cards.twoPeriodsAcumulated.employees.map((body, index) => (
            <tr key={`employee-${index.toString()}`}>
              <td className="employee-name">
                <DatatableEmployeeName
                  item={body}
                  fileName="fileInfo"
                  name="fullName"
                  fileUrl="fileUrl"
                  onClick={() => redirectToEmployee(body, tab)}
                />
              </td>
              <td>
                <ButtonTooltip
                  onClick={() => redirectToEmployee(body, tab)}
                  variant="circle-primary"
                  className="btn-circle"
                  size="sm"
                  text="Ver"
                >
                  <Icon icon="eye" />
                </ButtonTooltip>
              </td>
            </tr>
          ))
        ) : (
          <p>No se encontraron trabajadores</p>
        );
      const table = (
        <Table responsive>
          <tbody>{tableBody}</tbody>
        </Table>
      );
      setModalBody(table);
      setModalShow(true);
    }
  };

  useEffect(fetchDashboardCards, [moreData]);

  return (
    <div className="data-table-yellow">
      <Row className="m-top mb-2">
        <Col md={3} xs={12}>
          <CompactCard
            variant="info-grey"
            title={cards.totalLegalVacationsAvailable}
            subtitle="Días Vacaciones Legales"
            label="Días Legales Acumulados"
            active={activeCard === 'totalLegalVacationsAvailable'}
            onClick={() => handleClick('totalLegalVacationsAvailable')}
          />
        </Col>
        <Col md={3} xs={12}>
          <CompactCard
            variant="warning"
            title={cards.pending}
            subtitle="Días Por Autorizar"
            label="Días de Vacaciones"
            active={activeCard === 'pending'}
            onClick={() => handleClick('pending')}
          />
        </Col>
        <Col md={3} xs={12}>
          <CompactCard
            variant="danger"
            title={cards.rejected}
            subtitle="Días Vacaciones"
            label="Días Rechazados"
            active={activeCard === 'rejected'}
            onClick={() => handleClick('rejected')}
          />
        </Col>
        <Col md={3} xs={12}>
          <CompactCard
            variant="primary"
            title={cards.totalProgressiveVacationsAvailable}
            subtitle="Días de Vacaciones Progresivas"
            label="Días de Progresivas Acumuladas"
            active={activeCard === 'totalProgressiveVacationsAvailable'}
            onClick={() => handleClick('totalProgressiveVacationsAvailable')}
          />
        </Col>
        <Col md={3} xs={12}>
          <CompactCard
            variant="info-grey"
            title={cards.employeesOnVacation}
            subtitle="Trabajadores en Vacaciones"
            label="Trabajadores en Vacaciones"
            active={activeCard === 'onVacation'}
            onClick={() => handleClick('onVacation')}
          />
        </Col>
        <Col md={3} xs={12}>
          <CompactCard
            variant="warning"
            title={cards.daysDifferencePreviousYear ? `${cards.daysDifferencePreviousYear}%` : '-'}
            subtitle="Días tomados a la fecha respecto al año pasado"
            subtitleClass="font-weight-bold"
            label=""
            active={activeCard === 'differencePreviousYear'}
            onClick={() => handleClick('differencePreviousYear')}
          />
        </Col>
        <Col md={3} xs={12}>
          <CompactCard
            variant="danger"
            title={cards.twoPeriodsAcumulated?.amount}
            subtitle="Trabajadores con más de dos periodos acumulados"
            subtitleClass="font-weight-bold"
            label=""
            onClick={() => openModal('vacations')}
          />
        </Col>
      </Row>
      {'totalLegalVacationsAvailable totalProgressiveVacationsAvailable'.includes(activeCard) ? (
        <VacationAvailableDataTable
          defaultStartDate={defaultStartDate}
          defaultEndDate={defaultEndDate}
          columns={availableVacationColumns}
          moreData={moreData}
          setMoreData={setMoreData}
          withImport
          withMassActions
          customParams={{
            ...customParams,
            filter_vacation_exception: 'administrative_days',
            active_contract_today: true
          }}
        />
      ) : (
        <VacationDataTable
          withMassActions
          withWorkflow={false}
          defaultStartDate={defaultStartDate}
          defaultEndDate={defaultEndDate}
          columns={columns}
          moreData={moreData}
          withImport
          setMoreData={setMoreData}
          customParams={{
            ...customParams,
            filter_vacation_exception: 'administrative_days, administrative_days_openning_balance'
          }}
        />
      )}
      <DefaultModal
        body={modalBody}
        handleClose={() => setModalShow(false)}
        show={modalShow}
        modalSize="lg"
        title="Trabajadores con más de dos periodos acumulados"
        withConfirm={false}
      />
    </div>
  );
};

export default VacationIndex;
